import React, { useState, useEffect, ChangeEvent } from 'react';
import { Badge, Box, Button, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, ListItemIcon, Menu, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material';
import { Query, QueryDocumentSnapshot, collection, doc, endBefore, getCountFromServer, getDoc, getDocs, getFirestore, limit, limitToLast, or, orderBy, query, startAfter, startAt, where } from 'firebase/firestore';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { AddRounded, ArrowForwardIosRounded, CancelRounded, CheckCircleRounded, CheckRounded, FiberNewRounded, FilterListRounded, MopedRounded, PersonSearchRounded, RefreshRounded, RunCircleRounded } from '@mui/icons-material';
import { DateTime } from 'luxon';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import { Search, SearchIconWrapper, StyledInputBase } from '../../Search';
import { useDebouncedCallback } from 'use-debounce';
import { UmrahPaperRequest, UmrahPaperRequestStatus } from '../../../types/UmrahPaperRequest';
import umrahPaperRequestConverter from '../../../converters/umrahPaperRequestConverter';
import { updateUmrahPaperRequestStatus } from '../../../utils/updateUmrahPaperRequest';
import { UmrahPaperRequestForm } from './UmrahPaperRequestForm';
import { SecureComponent } from '../../../security/SecureComponent';

const getBadgeIconFromStatus = (status: UmrahPaperRequestStatus | 'All') => {
  if (status === 'All') return undefined;
  else if (status === 'Processing') return <RunCircleRounded color='warning' />;
  else if (status === 'Available') return <MopedRounded color='secondary' />;
  else if (status === 'Completed') return <CheckCircleRounded color='success' />;
  else if (status === 'Cancelled') return <CancelRounded color='error' />;
  return <FiberNewRounded color='info' />;
};

const getIconFromStatus = (status: UmrahPaperRequestStatus) => {
  if (status === 'Processing') return <RunCircleRounded />;
  else if (status === 'Available') return <MopedRounded />;
  else if (status === 'Completed') return <CheckCircleRounded />;
  else if (status === 'Cancelled') return <CancelRounded />;
  return <FiberNewRounded />;
};

const getColorFromStatus = (status: UmrahPaperRequestStatus) => {
  if (status === 'Processing') return 'warning';
  else if (status === 'Available') return 'secondary';
  else if (status === 'Completed') return 'success';
  else if (status === 'Cancelled') return 'error';
  return 'info';
};

export const UmrahPaperRequestsPage = () => {
  const db = getFirestore();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [umrahRequests, setUmrahRequests] = useState<UmrahPaperRequest[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<UmrahPaperRequestStatus | 'All'>('New');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);

  const [firstRow, setFirsRow] = useState<QueryDocumentSnapshot | null>(null);
  const [lastRow, setLastRow] = useState<QueryDocumentSnapshot | null>(null);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);

  const refresh = (callback?: (q: Query<UmrahPaperRequest>) => Query<UmrahPaperRequest>) => {
    setLoading(true);
    let q: Query<UmrahPaperRequest> = query(collection(db, `UmrahPaperRequests`)).withConverter(umrahPaperRequestConverter);

    if (search.trim() === '') 
      q = query(q, orderBy('dateRequested', 'asc'));
    else
      q = query(q, where('idNumber', '>=', search), where('idNumber', '<=', search + '\uf8ff'), orderBy('idNumber', 'asc'), orderBy('dateRequested', 'asc'));

    if(callback)
      q = callback(q);
    if (filter !== 'All')
      q = query(q, where('status', '==', filter));
    getDocs(q).then(qSnapshot => {
      setUmrahRequests(qSnapshot.docs.map(d => d.data()));
      setFirsRow(qSnapshot.docs[0]);
      setLastRow(qSnapshot.docs[qSnapshot.docs.length - 1]);
      setLoading(false);
    });

    // Count query
    let qCount: Query = query(collection(db, `UmrahPaperRequests`));
    if (search.trim() === '') 
      qCount = query(qCount, orderBy('dateRequested', 'asc'));
    else
      qCount = query(qCount, where('idNumber', '>=', search), where('idNumber', '<=', search + '\uf8ff'), orderBy('idNumber', 'asc'), orderBy('dateRequested', 'asc'));
    if (filter !== 'All') 
      qCount = query(qCount, where('status', '==', filter));
    getCountFromServer(qCount).then(f => setTotalRows(f.data().count));
  };

  const searchDebouncedCallback = useDebouncedCallback((value) => {
    refresh(q => query(q, limit(rowsPerPage)));
  }, 1000);

  const handleSearchOnChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
    searchDebouncedCallback(event.currentTarget.value);
  };

  const handleFormOpen = () => setShowForm(true);
  const handleFormClose = () => setShowForm(false);

  const handleFilterButtonOnClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterMenuOnClose = () => setFilterAnchorEl(null);

  const applyFilter = (f: UmrahPaperRequestStatus | 'All') => {
    setFilter(f);
    setFilterAnchorEl(null);
  }
  
  const filterAll = () => applyFilter('All');
  const filterNew = () => applyFilter('New');
  const filterProcessing = () => applyFilter('Processing');
  const filterAvailable = () => applyFilter('Available');
  const filterCompleted = () => applyFilter('Completed');

  const handlePageOnChange = (_e: React.MouseEvent<HTMLButtonElement> | null, to: number) => {
    if (to > page) {
      // Next
      refresh(q => {
        return query(q, startAfter(lastRow), limit(rowsPerPage));
      });
    } else if (to < page) {
      // Previous
      refresh(q => {
        return query(q, endBefore(firstRow), limitToLast(rowsPerPage));
      });
    } else {
      // Same
      refresh(q => {
        return query(q, startAt(firstRow), limit(rowsPerPage));
      });
    }
    setPage(to);
  };
  
  const handleRowsPerPageOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  
  const updateStatus = (umrahPaperRequest: UmrahPaperRequest, status: UmrahPaperRequestStatus) => {
    if (window.confirm(`Confirm to set status to '${status}'?`)) {
      updateUmrahPaperRequestStatus(umrahPaperRequest, status).then(() => {
        setUmrahRequests(umrahRequests.map(m => {
          if (m.id === umrahPaperRequest.id) {
            return { ...m, status};
          }

          return m;
        }));
        enqueueSnackbar(`Umrah paper request has been set to '${status}'!`, { variant: 'success' });
      });
    }
  };

  const handleFormOnSave = (m: UmrahPaperRequest) => {
    refresh(q => query(q, limit(rowsPerPage)));
    setShowForm(false);
  };

  useEffect(() => {
    refresh(q => query(q, limit(rowsPerPage)));
  }, [filter, rowsPerPage]);

  return (
    <>
      <Container disableGutters>
        <Typography variant="h5" mt={2} mb={5}>Umrah Paper Requests</Typography>
        <Stack direction='row' mb={1}>
          <Box width={650}>
            <Search>
              <SearchIconWrapper>
                <PersonSearchRounded color='primary' />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search employee by Id Number"
                inputProps={{ 'aria-label': 'search' }}
                value={search}
                onChange={handleSearchOnChanged}
              />
            </Search>
          </Box>
          <SecureComponent permissions={['create.umrahRequests']}>
            <Button startIcon={<AddRounded />} onClick={handleFormOpen}>Create New Request</Button>
          </SecureComponent>
          <Box flexGrow={1} />
        </Stack>
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={76} align="right">Count</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell width={220}>Date</TableCell>
                  <TableCell width={150}>
                    <Stack 
                      direction='row' 
                      alignItems='center'
                      sx={{
                        backgroundColor: '#0000001f',
                        borderRadius: 2,
                        borderWidth: 1,
                        borderColor: '#0000004f',
                        borderStyle: 'solid',
                        padding: 1,
                      }}
                    >
                      <Box flexGrow={1}>
                        Status
                      </Box>
                      <Tooltip title='Filter Status'>
                        <IconButton size='small' onClick={handleFilterButtonOnClick}>
                          <Badge badgeContent={filter !== 'All' ? getBadgeIconFromStatus(filter) : 0}>
                            <FilterListRounded />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <SecureComponent permissions={['update.umrahRequests']}>
                    <TableCell width={168} align='center'>Actions</TableCell>
                  </SecureComponent>
                  <TableCell width={72} align='center'>
                    <Tooltip title='Refresh'>
                      <IconButton color='info' onClick={() => refresh(q => query(q, limit(rowsPerPage)))}><RefreshRounded /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {umrahRequests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant='caption'>
                        No records exist.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {umrahRequests.map((u, i) => {
                  return (
                    <TableRow 
                      key={u.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      hover
                    >
                      <TableCell align="right">{(i + 1) + (page * rowsPerPage)}</TableCell>
                      <TableCell>
                        <Box>
                          <Box display="flex" flexDirection="row" alignItems="center">
                            <Typography variant='body1'><strong>{u.name}</strong></Typography>
                          </Box>
                          <Typography variant="caption">{u.idNumber}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{DateTime.fromISO(u.date).toFormat('MMMM dd, yyyy')}</TableCell>
                      <TableCell>
                        <Chip variant='outlined' color={getColorFromStatus(u.status)} icon={getIconFromStatus(u.status)} label={u.status} />
                      </TableCell>
                      <SecureComponent permissions={['update.umrahRequests']}>
                        <TableCell>
                          <Stack direction='row' spacing={1}>
                            <Tooltip title='Set as Processing'>
                              <span>
                                <IconButton color='warning' disabled={u.status !== 'New'} onClick={() => updateStatus(u, 'Processing')}><RunCircleRounded /></IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title='Set as Ready for Pickup'>
                              <span>
                                <IconButton color='secondary' disabled={u.status !== 'New' && u.status !== 'Processing'} onClick={() => updateStatus(u, 'Available')}><MopedRounded /></IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title='Set as Completed'>
                              <span>
                                <IconButton color='success' disabled={u.status === 'Completed' || u.status === 'Cancelled'} onClick={() => updateStatus(u, 'Completed')}><CheckRounded /></IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </SecureComponent>
                      <TableCell>
                        <Tooltip title='View'>
                          <IconButton color='info' onClick={() => navigate(`/dashboard/umrahRequests/${u.id}`)}><ArrowForwardIosRounded /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[25, 50, 100]}
            count={totalRows}
            page={page}
            rowsPerPage={rowsPerPage ?? 25}
            SelectProps={{
              inputProps: {
                'aria-label': 'rows per page',
              },
            }}
            onPageChange={handlePageOnChange}
            onRowsPerPageChange={handleRowsPerPageOnChange}
            ActionsComponent={TablePaginationActions} />
        </Paper>
      </Container>
      <SecureComponent permissions={['create.umrahRequests']}>
        <Dialog open={showForm} onClose={handleFormClose} fullWidth>
          <DialogTitle>Create Umrah Paper Request</DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 0 }}>
            <UmrahPaperRequestForm onClosed={handleFormClose} onSaved={handleFormOnSave} />
          </DialogContent>
        </Dialog>
      </SecureComponent>
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterMenuOnClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 12,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={filterAll}>
          <ListItemIcon></ListItemIcon>
          All
        </MenuItem>
        <MenuItem onClick={filterNew}>
          <ListItemIcon>
            <FiberNewRounded color='info' />
          </ListItemIcon>
          New
        </MenuItem>
        <MenuItem onClick={filterProcessing}>
          <ListItemIcon>
            <RunCircleRounded color='warning' />
          </ListItemIcon>
          Processing
        </MenuItem>
        <MenuItem onClick={filterAvailable}>
          <ListItemIcon>
            <MopedRounded color='secondary' />
          </ListItemIcon>
          Available
        </MenuItem>
        <MenuItem onClick={filterCompleted}>
          <ListItemIcon>
            <CheckCircleRounded color='success' />
          </ListItemIcon>
          Completed
        </MenuItem>
      </Menu>
    </>
  );
};
